import { digger } from '@cbre/digger';
import { defaultTxt, formatNumber } from '@miq/utils';
import { ReportRecord } from '../../../../../models/report.model';
import { DisplayFnArgumentsInfo, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { PropertyType, propertyTypeDisplayFnFactory } from '../us.common';
import {
  getAreaLeased,
  getAskingRent,
  getBaseRent,
  getBuiltReno,
  getClass,
  getEffectiveRent,
  getEscalations,
  getFirstElementOrGiven,
  getFormatedUnicValueInArrayAsString,
  getFormattedAddress,
  getFreeRentMonths,
  getLeaseImage,
  getLeaseTerm,
  getLessorData,
  getLessorRepData,
  getLocation,
  getOPEX,
  getpPropertySize,
  getPropertySubType,
  getRentSteps,
  getSubLessorData,
  getSubLessorRepData,
  getSuites,
  getTenantRep,
  getTenat,
  getTIA,
  isFieldVisible,
  isLessorVisible,
  LONG_LIST,
  SHORT_LIST1,
  SHORT_LIST2,
} from './use-lease-condensed-utils';

const industrialDisplayFnWrapper = propertyTypeDisplayFnFactory(PropertyType.Industrial);

export const industrialDisplayFnMap: TemplateFieldsDisplayFn = {
  /*
   * NOTE 1: Follow the next formatting
   *   [Math.random()]: {
   *     // # - The Name of the Field
   *     displayFn: () => {}
   *   },
   * NOTE 2: For simple fields (one single argument/column ID and no custom logic)
   *         you can use `displayFn: displayFnWrapper`
   * NOTE 3: you can get the record value using the mapAndFormatDynamicValue function
   *         i.e. mapAndFormatDynamicValue(record, arg)
   */
  556: {
    // # - Property Name
    displayFn: displayFnWrapper,
  },
  557: {
    // # - Property Address
    displayFn: getFormattedAddress,
  },
  558: {
    // # - Property Location
    displayFn: getLocation,
  },
  561: {
    // # - Property Size
    displayFn: getpPropertySize,
  },
  562: {
    // # - Property Type - SubType
    displayFn: getPropertySubType,
  },
  564: {
    // # - Class Type
    displayFn: getClass,
  },
  565: {
    // # - Tenancy Type
    displayFn: displayFnWrapper,
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST2, LONG_LIST]),
  },
  566: {
    // #- Built|Reno
    displayFn: getBuiltReno,
  },
  /** ################################################################################# */
  /** LEASE COMP INFO SECTION */
  567: {
    // # - Area Leased (Deal Size)
    displayFn: getAreaLeased,
  },
  568: {
    // # - Sign Date
    displayFn: displayFnWrapper,
  },
  569: {
    // # - Lease Term (Months)
    displayFn: getLeaseTerm,
  },
  570: {
    // # - Lease Term -Start Date
    displayFn: displayFnWrapper,
  },
  571: {
    // # - Lease Term -End Date
    displayFn: displayFnWrapper,
  },
  572: {
    // # - Parking Ratio
    displayFn: (record: ReportRecord, _, parkingRatio) => {
      const parkingRatioVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, parkingRatio));
      return parkingRatioVal != null && parkingRatioVal !== defaultTxt ? `${parkingRatioVal} / 1000` : defaultTxt;
    },
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST2, LONG_LIST]),
  },
  573: {
    // # - Base Rent
    displayFn: getBaseRent,
  },
  600: {
    // # - Effective Rent
    displayFn: getEffectiveRent,
  },
  601: {
    // # - Asking Rent
    displayFn: getAskingRent,
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST2, LONG_LIST]),
  },
  602: {
    // # - Escalations
    displayFn: getEscalations,
  },
  603: {
    // # - Free Rent Months
    displayFn: getFreeRentMonths,
  },
  604: {
    // # - TIA/Tis
    displayFn: getTIA,
  },
  605: {
    // # - OPEX (Expenses)
    displayFn: getOPEX,
  },
  606: {
    // # - Rent Review / Rent Steps
    displayFn: (
      record: ReportRecord,
      options,
      escalationOrder,
      escalationAmount,
      escalationPercent,
      escalationPeriod
    ) => getRentSteps(record, options, escalationOrder, escalationAmount, escalationPercent, escalationPeriod),
    isVisible: (record: ReportRecord) => isFieldVisible(record, [LONG_LIST]),
  },
  607: {
    // # - Transaction Type
    displayFn: displayFnWrapper,
  },
  608: {
    // # - Notes
    displayFn: displayFnWrapper,
  },
  609: {
    // # - Comp ID
    displayFn: displayFnWrapper,
  },
  610: {
    // # - Source Info
    displayFn: displayFnWrapper,
  },
  /** ################################################################################# */
  /** INDUSTRIAL SPECIFICATIONS */
  611: {
    // # - SUITE
    displayFn: getSuites,
  },
  612: {
    // # - Reserved Parking Spaces
    displayFn: getFormatedUnicValueInArrayAsString,
  },
  613: {
    // # - Doors
    displayFn: (record: ReportRecord, _, gLDoorCount, dHDoorCount, propertyDHDoorCount, propertyGLDoorCount) => {
      // propertyGLDoorCount backup of gLDoorCount
      // propertyDHDoorCount backup of dHDoorCount
      const industrialGLDoorCountVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, gLDoorCount));
      const propertyGLDoorCountVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, propertyGLDoorCount));
      const industrialDHDoorCountVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, dHDoorCount));
      const propertyDHDoorCountVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, propertyDHDoorCount));

      // Example: 2DH/4GL or -/-
      let glDoorCountVal =
        !industrialGLDoorCountVal || industrialGLDoorCountVal === defaultTxt
          ? propertyGLDoorCountVal
          : industrialGLDoorCountVal;
      glDoorCountVal = glDoorCountVal && glDoorCountVal !== defaultTxt ? `${glDoorCountVal}GL` : defaultTxt;

      let dhDoorCountVal =
        !industrialDHDoorCountVal || industrialDHDoorCountVal === defaultTxt
          ? propertyDHDoorCountVal
          : industrialDHDoorCountVal;
      dhDoorCountVal = dhDoorCountVal && dhDoorCountVal !== defaultTxt ? `${dhDoorCountVal}DH` : defaultTxt;

      return `${dhDoorCountVal}/${glDoorCountVal}`;
    },
  },
  614: {
    // # - Power
    displayFn: (record: ReportRecord, _, ampDsc, voltDsc, propertyVolt, propertyAmp) => {
      const ampDscr = mapAndFormatDynamicValue(record, ampDsc); // Primary
      const vltDscr = mapAndFormatDynamicValue(record, voltDsc); // Primary
      const ampRange = mapAndFormatDynamicValue(record, propertyAmp); // Bakcup
      const vltRange = mapAndFormatDynamicValue(record, propertyVolt); // Backup

      const finalAmp = !ampDscr || ampDscr === defaultTxt ? ampRange : ampDscr;
      const finalVolt = !vltDscr || vltDscr === defaultTxt ? vltRange : vltDscr;
      return `${finalAmp || defaultTxt}/${finalVolt || defaultTxt}`;
    },
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST1, LONG_LIST]),
  },
  615: {
    // # - Office Space
    displayFn: (record: ReportRecord, _, totalSpaceField, availableSpaceField) => {
      const availableOfficeSpace = digger(record.data, availableSpaceField.field);
      const totalSpace = Number(digger(record.data, totalSpaceField.field));
      const availableOfficeArea = Number(digger(record.data, availableSpaceField.field));
      // validate that both values are valid numbers
      if (isNaN(totalSpace) || isNaN(availableOfficeArea)) {
        return defaultTxt;
      }
      const percentageValue = totalSpace > 0 ? (availableOfficeArea / totalSpace) * 100 : 0;
      return availableOfficeSpace != null && percentageValue > 0
        ? `${formatNumber(availableOfficeSpace)} SF | ${formatNumber(percentageValue)}%`
        : defaultTxt;
    },
  },
  616: {
    // # - Rail
    displayFn: (record: ReportRecord, options, rail, railProperty) => {
      const railVal = propertyTypeDisplayFnFactory(PropertyType.Industrial)(record, options, rail);
      const propertyRailVal = propertyTypeDisplayFnFactory(PropertyType.Industrial)(record, options, railProperty);
      const railLabel = !railVal || railVal === defaultTxt ? propertyRailVal : railVal;
      return railLabel || defaultTxt;
    },
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST1, LONG_LIST]),
  },
  617: {
    // # - Clear Height
    displayFn: (record: ReportRecord, options, clearHeight: DisplayFnArgumentsInfo) => {
      let clearHeightVal = propertyTypeDisplayFnFactory(PropertyType.Industrial)(record, options, clearHeight);
      clearHeightVal = getFirstElementOrGiven(clearHeightVal);
      return clearHeightVal ? clearHeightVal : defaultTxt;
    },
  },
  618: {
    // # - Sprinkler
    displayFn: industrialDisplayFnWrapper,
  },
  619: {
    // # - yard
    displayFn: industrialDisplayFnWrapper,
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST1, LONG_LIST]),
  },
  620: {
    // # - Tenant
    displayFn: getTenat,
  },
  621: {
    // # - Tenant Rep
    displayFn: getTenantRep,
    isVisible: (_: ReportRecord, { reportOptions }) => {
      return !reportOptions.brokerAgency;
    },
  },
  // 622 - Lessor
  622: {
    displayFn: getLessorData,
    isVisible: isLessorVisible,
  },
  // 188- SubLessor
  188: {
    displayFn: getSubLessorData,
    isVisible: record => !isLessorVisible(record as ReportRecord),
  },
  // 623 - Lessor Rep
  623: {
    displayFn: getLessorRepData,
    isVisible: (record, settings) => {
      if (settings?.reportOptions?.brokerAgency) {
        return !settings?.reportOptions?.brokerAgency;
      }
      return isLessorVisible(record as ReportRecord);
    },
  },
  // 187 - SubLessor Rep
  187: {
    displayFn: getSubLessorRepData,
    isVisible: (record, settings) => {
      if (settings?.reportOptions?.brokerAgency) {
        return !settings?.reportOptions?.brokerAgency;
      }
      return !isLessorVisible(record as ReportRecord);
    },
  },
  /*
   * NOTE:(gaby): we were supposed to use 98 & 99 for the Lease images
   * and 528 & 527 for property images. However, due to existing images logic
   * implemented in the report-field-formatter.service, we cannot use 98 & 99 to
   * return the custom data and data structure we need for each availabilities.
   *
   * Therefore, 98 & 99 will be used for property images & 528 & 527 for the
   * availabilities images
   */
  527: {
    displayFn: getLeaseImage,
  },
  895: {
    // # - Total Land Area
    displayFn: displayFnWrapper,
  },
};
